import {
	isArchived,
	isHoldActive,
	type BundleInventoryItem,
} from "@internal/backstage-plugin-avd-common";

import type { BundleInventoryDescriptor } from "./bundleDescriptors";
import { bundleIacVersion } from "./bundleDescriptors";
import { deriveHealth, type HealthLevel } from "./bundleHealth";

export const DRIFT_FILTERS = ["drifted", "in-sync", "error", "unchecked"] as const;
export const HEALTH_FILTERS = ["failed", "attention", "healthy"] as const;
export const LIFECYCLE_FILTERS = ["active", "expiring"] as const;
export const HELD_FILTERS = ["held", "not-held"] as const;
export const IAC_FILTERS = ["behind", "current"] as const;
export const ARCHIVED_FILTERS = ["include"] as const;
export type DriftFilter = (typeof DRIFT_FILTERS)[number];
export type LifecycleFilter = (typeof LIFECYCLE_FILTERS)[number];
export type HeldFilter = (typeof HELD_FILTERS)[number];
export type IacFilter = (typeof IAC_FILTERS)[number];
export type ArchivedFilter = (typeof ARCHIVED_FILTERS)[number];

/** The groups the Filters popover offers; each is a repeatable URL parameter. */
export const FILTER_GROUPS = [
	"health",
	"type",
	"status",
	"drift",
	"lifecycle",
	"held",
	"iac",
	"archived",
] as const;
export type FilterGroup = (typeof FILTER_GROUPS)[number];

/** Every filter the inventory offers. Values in a group OR together, groups AND; each lives in the URL. */
export type InventoryFilters = {
	/** Bundle types. */
	type?: string[];
	/** Colleague: matches name, UPN or slug. */
	q?: string;
	/** Status bucket ids from a type's descriptor. */
	status?: string[];
	health?: HealthLevel[];
	drift?: DriftFilter[];
	lifecycle?: LifecycleFilter[];
	/** Whether a hold is active. */
	held?: HeldFilter[];
	/** The recorded IaC version against the one this build was stamped with. */
	iac?: IacFilter[];
	/** Archived rows are hidden unless this is set to include them. */
	archived?: ArchivedFilter[];
	/** One section per colleague, with their base. A view, not a filter. */
	group?: "colleague";
};

/** The distinct values of a repeated parameter; an `allowed` list drops what this page does not know. */
function distinct<T extends string>(raw: string[], allowed?: readonly T[]): T[] | undefined {
	const found = [...new Set(raw.filter(Boolean))].filter(
		(v) => !allowed || (allowed as readonly string[]).includes(v),
	);
	return found.length ? (found as T[]) : undefined;
}

/** Reads filters from a URL; a value this page does not know is dropped, never trusted. */
export function parseFilters(params: URLSearchParams): InventoryFilters {
	const all = (key: string) => params.getAll(key);
	// Legacy `held=1` and `behind=1` links still parse.
	const held = all("held").map((v) => (v === "1" ? "held" : v));
	const behind = params.get("behind") === "1" ? ["behind"] : [];
	return {
		type: distinct(all("type")),
		q: params.get("q") || undefined,
		status: distinct(all("status")),
		health: distinct(all("health"), HEALTH_FILTERS),
		drift: distinct(all("drift"), DRIFT_FILTERS),
		lifecycle: distinct(all("lifecycle"), LIFECYCLE_FILTERS),
		held: distinct(held, HELD_FILTERS),
		iac: distinct([...all("iac"), ...behind], IAC_FILTERS),
		archived: distinct(all("archived"), ARCHIVED_FILTERS),
		group: params.get("group") === "colleague" ? "colleague" : undefined,
	};
}

/** The URL form of a set of filters: one entry per value; an unset filter leaves no parameter. */
export function toSearchParams(filters: InventoryFilters): URLSearchParams {
	const params = new URLSearchParams();
	if (filters.q) params.set("q", filters.q);
	for (const group of FILTER_GROUPS) {
		for (const value of filters[group] ?? []) params.append(group, value);
	}
	if (filters.group) params.set("group", filters.group);
	return params;
}

/** Whether any filter narrows the rows. Grouping is a view and Show archived widens, so neither is one. */
export const hasFilters = (filters: InventoryFilters): boolean =>
	toSearchParams({ ...filters, group: undefined, archived: undefined }).size > 0;

/** How many values the Filters popover has selected; the search box and grouping are not among them. */
export const activeFilterCount = (filters: InventoryFilters): number =>
	FILTER_GROUPS.reduce((n, group) => n + (filters[group]?.length ?? 0), 0);

/** Every filter off but the view: what Clear filters leaves. */
export const clearFilters = (filters: InventoryFilters): InventoryFilters => ({
	group: filters.group,
});

/** Every Filters group off, keeping the search and the view: what Clear all leaves. */
export const clearPopoverFilters = (filters: InventoryFilters): InventoryFilters => ({
	...clearFilters(filters),
	q: filters.q,
});

/** `drifted` includes what a type's own estate read calls drift; `unchecked` is never checked. */
function matchesDrift(
	item: BundleInventoryItem,
	drift: DriftFilter,
	descriptor: BundleInventoryDescriptor,
): boolean {
	switch (drift) {
		case "drifted":
			return item.driftStatus === "drifted" || Boolean(descriptor.isEstateDrifted?.(item));
		case "in-sync":
			return item.driftStatus === "in-sync";
		case "error":
			return item.driftStatus === "error";
		case "unchecked":
			return item.driftStatus === undefined;
	}
}

/** The colleague text a search matches: owner, slug and whatever a type adds. */
export function searchText(
	item: BundleInventoryItem,
	descriptor: BundleInventoryDescriptor,
): string {
	return [
		item.owner.upn,
		item.owner.displayName,
		item.slug,
		item.bundleId,
		descriptor.searchText?.(item),
	]
		.filter(Boolean)
		.join(" ")
		.toLowerCase();
}

/** Whether a row passes every set filter. */
export function matchesFilters(
	item: BundleInventoryItem,
	filters: InventoryFilters,
	descriptor: BundleInventoryDescriptor,
	now: Date = new Date(),
): boolean {
	if (isArchived(item) && !filters.archived?.includes("include")) return false;
	if (filters.type && !filters.type.includes(item.bundleType)) return false;
	if (filters.q) {
		if (!searchText(item, descriptor).includes(filters.q.toLowerCase())) return false;
	}
	if (filters.status && !filters.status.some((b) => descriptor.matchesBucket(b, item))) {
		return false;
	}
	if (filters.health && !filters.health.includes(deriveHealth(item, descriptor, now).level)) {
		return false;
	}
	if (filters.drift && !filters.drift.some((d) => matchesDrift(item, d, descriptor))) {
		return false;
	}
	if (filters.lifecycle && !filters.lifecycle.includes(item.lifecycle)) return false;
	if (filters.held) {
		const held = isHoldActive(item.holdUntil, now);
		if (!filters.held.some((h) => (h === "held") === held)) return false;
	}
	if (filters.iac) {
		const state = bundleIacVersion(item, descriptor).state;
		if (!filters.iac.some((v) => v === state)) return false;
	}
	return true;
}

/** The four measure tiles: each is a shortcut that sets exactly one filter group to fixed values. */
export const TILE_FILTERS = {
	ready: { group: "status", values: ["ready"] },
	attention: { group: "health", values: ["failed", "attention"] },
	expiring: { group: "lifecycle", values: ["expiring"] },
	behind: { group: "iac", values: ["behind"] },
} as const satisfies Record<string, { group: FilterGroup; values: readonly string[] }>;
export type TileId = keyof typeof TILE_FILTERS;

/** Whether a tile's group holds exactly the tile's values (any order). */
export function isTileSelected(filters: InventoryFilters, tile: TileId): boolean {
	const { group, values } = TILE_FILTERS[tile];
	const current: readonly string[] = filters[group] ?? [];
	return current.length === values.length && values.every((v) => current.includes(v));
}

/** Pressing a tile sets its group to its values; pressing it again clears the group. Other groups stay. */
export function toggleTile(filters: InventoryFilters, tile: TileId): InventoryFilters {
	const { group, values } = TILE_FILTERS[tile];
	return { ...filters, [group]: isTileSelected(filters, tile) ? undefined : [...values] };
}
